import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Loader2,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DocumentPreviewModal - A modal component for previewing documents
 * Supports: Images (JPG, PNG, WebP, HEIC), PDFs
 * Features: Zoom, Rotate (images), Fullscreen, Download
 */
export default function DocumentPreviewModal({
  isOpen,
  onClose,
  document: docData,
  claimNumber,
  token,
  documents = [], // All documents for navigation
  currentIndex = 0,
  onNavigate
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isImage = docData?.mime_type?.startsWith('image/') || 
    ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(
      docData?.filename?.toLowerCase().split('.').pop()
    );
  
  const isPdf = docData?.mime_type === 'application/pdf' ||
    docData?.filename?.toLowerCase().endsWith('.pdf');

  const isPreviewable = isImage || isPdf;

  // Fetch document for preview
  useEffect(() => {
    if (!isOpen || !docData || !claimNumber || !token) return;

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      setZoom(1);
      setRotation(0);

      try {
        const response = await fetch(
          `${API_URL}/api/documents/${claimNumber}/${docData.document_id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load document');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();

    // Cleanup
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, docData, claimNumber, token]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && documents.length > 1) {
        handlePrev();
      } else if (e.key === 'ArrowRight' && documents.length > 1) {
        handleNext();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, documents.length, currentIndex]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      window.document.body.style.overflow = 'hidden';
    } else {
      window.document.body.style.overflow = '';
    }
    return () => {
      window.document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  
  const handlePrev = () => {
    if (onNavigate && currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };
  
  const handleNext = () => {
    if (onNavigate && currentIndex < documents.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleDownload = () => {
    if (!previewUrl || !docData) return;
    
    const a = window.document.createElement('a');
    a.href = previewUrl;
    a.download = docData.filename || 'document';
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className={`relative bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
          isFullscreen 
            ? 'w-full h-full rounded-none' 
            : 'w-[95vw] h-[90vh] max-w-6xl'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-medium text-white truncate">
                {docData?.filename || 'Document Preview'}
              </h3>
              <p className="text-xs text-slate-400">
                {docData?.document_type_label || docData?.document_type}
                {documents.length > 1 && ` • ${currentIndex + 1} of ${documents.length}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Zoom Controls (for images) */}
            {isImage && !loading && !error && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomOut}
                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                  title="Zoom Out (-)"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-xs text-slate-400 w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleZoomIn}
                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                  title="Zoom In (+)"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRotate}
                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                  title="Rotate"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-slate-700 mx-1" />
              </>
            )}
            
            {/* Fullscreen Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
            
            {/* Download */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              disabled={loading || error}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
            
            {/* Close */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
              title="Close (Esc)"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 relative overflow-hidden bg-slate-950">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin mx-auto mb-3" />
                <p className="text-slate-400">Loading preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center max-w-md px-4">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                <p className="text-white font-medium mb-2">Failed to load preview</p>
                <p className="text-slate-400 text-sm mb-4">{error}</p>
                <Button
                  onClick={handleDownload}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Instead
                </Button>
              </div>
            </div>
          ) : !isPreviewable ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center max-w-md px-4">
                <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">Preview not available</p>
                <p className="text-slate-400 text-sm mb-4">
                  This file type ({docData?.filename?.split('.').pop()?.toUpperCase()}) cannot be previewed in browser.
                </p>
                <Button
                  onClick={handleDownload}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download File
                </Button>
              </div>
            </div>
          ) : isImage ? (
            <div className="absolute inset-0 overflow-auto flex items-center justify-center p-4">
              <img
                src={previewUrl}
                alt={docData?.filename}
                className="max-w-none transition-transform duration-200"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  maxHeight: zoom === 1 ? '100%' : 'none',
                  maxWidth: zoom === 1 ? '100%' : 'none'
                }}
                draggable={false}
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={`${previewUrl}#toolbar=0&navpanes=0`}
              className="w-full h-full border-0"
              title={docData?.filename}
            />
          ) : null}
          
          {/* Navigation Arrows */}
          {documents.length > 1 && !loading && !error && (
            <>
              {currentIndex > 0 && (
                <button
                  onClick={handlePrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}
              {currentIndex < documents.length - 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  title="Next (→)"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              )}
            </>
          )}
        </div>
        
        {/* Footer - Thumbnail Navigation */}
        {documents.length > 1 && (
          <div className="px-4 py-3 bg-slate-800 border-t border-slate-700">
            <div className="flex items-center justify-center gap-2 overflow-x-auto pb-1">
              {documents.map((doc, idx) => (
                <button
                  key={doc.document_id}
                  onClick={() => onNavigate && onNavigate(idx)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentIndex
                      ? 'border-blue-500 ring-2 ring-blue-500/30'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                  title={doc.filename}
                >
                  <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, window.document.body);
}
